import { useState } from 'react';
import { Plus, Trash2, Edit2 } from 'lucide-react';

interface ForecastDimensionsTabProps {
  forecastData: any;
}

export default function ForecastDimensionsTab({ forecastData }: ForecastDimensionsTabProps) {
  const {
    scenario,
    dimensions,
    dimensionValues,
    createDimension,
    deleteDimension,
    setDimensionValue
  } = forecastData;

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newDimCode, setNewDimCode] = useState('');
  const [newDimLabel, setNewDimLabel] = useState('');
  const [newDimUnit, setNewDimUnit] = useState('');

  const handleCreateDimension = async () => {
    if (!newDimCode.trim() || !newDimLabel.trim() || !newDimUnit.trim()) {
      alert('Veuillez remplir tous les champs');
      return;
    }

    try {
      await createDimension({
        scenario_id: scenario.id,
        code: newDimCode.trim(),
        label: newDimLabel.trim(),
        unit_name: newDimUnit.trim()
      });

      setNewDimCode('');
      setNewDimLabel('');
      setNewDimUnit('');
      setShowCreateModal(false);
    } catch (error) {
      alert('Erreur lors de la création');
    }
  };

  const handleDeleteDimension = async (id: string) => {
    if (!confirm('Supprimer cette dimension ? Les lignes qui l\'utilisent seront affectées.')) {
      return;
    }

    try {
      await deleteDimension(id);
    } catch (error) {
      alert('Erreur lors de la suppression');
    }
  };

  const handleValueChange = async (dimensionId: string, yearIndex: number, value: string) => {
    const numValue = parseFloat(value) || 0;
    try {
      await setDimensionValue(dimensionId, yearIndex, numValue);
    } catch (error) {
      console.error('Error updating value:', error);
    }
  };

  const getDimensionValue = (dimensionId: string, yearIndex: number): number => {
    const val = dimensionValues.find(
      dv => dv.dimension_id === dimensionId && dv.year_index === yearIndex
    );
    return val ? val.value : 0;
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Dimensions d'activité</h2>
          <p className="text-sm text-gray-600 mt-1">
            Définissez des indicateurs d'activité (ex: nombre d'élèves, de sites) pour calculer
            automatiquement certaines lignes
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus size={18} />
          <span className="hidden sm:inline">Ajouter une dimension</span>
          <span className="sm:hidden">Ajouter</span>
        </button>
      </div>

      {dimensions.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
          <p className="text-gray-600">
            Aucune dimension créée. Les dimensions sont optionnelles mais utiles pour des calculs
            automatiques (ex: coût par élève).
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {dimensions.map(dim => (
            <div key={dim.id} className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-mono rounded">
                      {dim.code}
                    </span>
                    <h3 className="font-medium text-gray-900">{dim.label}</h3>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">Unité: {dim.unit_name}</p>
                </div>
                <button
                  onClick={() => handleDeleteDimension(dim.id)}
                  className="text-gray-400 hover:text-red-600 transition-colors"
                >
                  <Trash2 size={18} />
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full min-w-[500px]">
                  <thead>
                    <tr className="border-b border-gray-200">
                      {Array.from({ length: scenario.years_count }, (_, i) => i + 1).map(
                        yearIndex => (
                          <th
                            key={yearIndex}
                            className="px-3 py-2 text-left text-xs font-medium text-gray-600"
                          >
                            Année {yearIndex}
                          </th>
                        )
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      {Array.from({ length: scenario.years_count }, (_, i) => i + 1).map(
                        yearIndex => (
                          <td key={yearIndex} className="px-3 py-2">
                            <input
                              type="number"
                              min="0"
                              step="1"
                              value={getDimensionValue(dim.id, yearIndex)}
                              onChange={e =>
                                handleValueChange(dim.id, yearIndex, e.target.value)
                              }
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </td>
                        )
                      )}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Nouvelle dimension</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Code (ex: CAP1, SITES) *
                </label>
                <input
                  type="text"
                  value={newDimCode}
                  onChange={e => setNewDimCode(e.target.value.toUpperCase())}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="CAP1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Libellé *
                </label>
                <input
                  type="text"
                  value={newDimLabel}
                  onChange={e => setNewDimLabel(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Élèves CAP 1ère année"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom de l'unité *
                </label>
                <input
                  type="text"
                  value={newDimUnit}
                  onChange={e => setNewDimUnit(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="élèves"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setNewDimCode('');
                  setNewDimLabel('');
                  setNewDimUnit('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleCreateDimension}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Créer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
